import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { ListTodo, Wallet, Inbox, HandCoins, AlertTriangle } from "lucide-react";
import { Dot, ProgressBar, pct } from "@/components/monitoring/shared";

const Stat = ({ icon: Icon, label, value, sub, tone = "slate", testId, href }) => (
  <a href={href} className="block">
    <Card className="border-slate-200 hover:border-slate-300 transition-colors">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="min-w-0">
            <div className="text-[11px] font-medium uppercase tracking-wider text-slate-500">{label}</div>
            <div className="mt-1 font-display font-bold text-2xl" data-testid={testId}>{value ?? <Skeleton className="h-7 w-16" />}</div>
            {sub && <div className="text-xs text-slate-500 mt-0.5 truncate">{sub}</div>}
          </div>
          <div className={`w-9 h-9 rounded-lg bg-${tone}-100 flex items-center justify-center shrink-0`}><Icon className={`w-4 h-4 text-${tone}-600`} /></div>
        </div>
      </CardContent>
    </Card>
  </a>
);

const HONORIFICS = /^(dr|mr|mrs|ms|miss|prof|shri|smt|sri|er|adv|ca)\.?$/i;
const greetName = (name) => {
  const w = (name || "").trim().split(/\s+/).filter(Boolean);
  if (!w.length) return "";
  const hon = HONORIFICS.test(w[0]) ? `${w[0].replace(/\.$/, "")}.` : null;
  const rest = hon ? w.slice(1) : w;
  const core = rest.length > 1 ? [rest[0], rest[rest.length - 1]] : rest;
  return [hon, ...core].filter(Boolean).join(" ");
};

const Loading = ({ n }) => <div className="space-y-2 p-4">{Array.from({ length: n }).map((_, i) => <Skeleton key={i} className="h-7 w-full" />)}</div>;

function Glance({ rows }) {
  return (
    <Card data-testid="dash-glance"><CardContent className="p-0">
      <div className="px-4 py-3 border-b flex items-center justify-between"><div className="text-sm font-semibold">My projects at a glance</div>
        {rows?.length > 0 && <a href="/app/monitoring" className="text-xs text-blue-700" data-testid="dash-glance-all">See all ({rows.length}) →</a>}</div>
      {!rows ? <Loading n={4} /> : rows.length === 0 ? <div className="p-4 text-sm text-slate-500">No projects yet.</div> : rows.slice(0, 6).map(r => (
        <div key={r.id} className="grid grid-cols-12 gap-3 items-center px-4 py-2 border-t first:border-0 text-sm" data-testid={`dash-glance-${r.code}`}>
          <div className="col-span-5 flex items-center gap-2 min-w-0"><Dot rag={r.rag} /><span className="font-mono text-xs font-semibold">{r.code}</span><span className="text-xs text-slate-500 truncate">{r.name}</span></div>
          <div className="col-span-4">{r.progress == null ? <span className="text-xs text-slate-400">Set targets · time {pct(r.time_elapsed)}</span>
            : <div className="flex items-center gap-2"><ProgressBar value={r.progress} marker={r.time_elapsed} rag={r.rag} /><span className="font-mono text-xs w-10 text-right">{pct(r.progress)}</span></div>}</div>
          <div className="col-span-2 text-xs text-slate-600 text-right">Budget {pct(r.budget_util)}</div>
          <a href={`/app/monitoring?project=${r.id}`} className="col-span-1 text-xs text-blue-700 text-right" data-testid={`dash-glance-view-${r.code}`}>View</a>
        </div>))}
    </CardContent></Card>
  );
}

function WeekTasks({ tasks }) {
  return (
    <Card data-testid="dash-week-tasks"><CardContent className="p-0">
      <div className="px-4 py-3 border-b flex items-center justify-between"><div className="text-sm font-semibold">My tasks this week</div>
        <a href="/app/tasks" className="text-xs text-blue-700" data-testid="dash-tasks-all">View all →</a></div>
      {!tasks ? <Loading n={3} /> : tasks.length === 0 ? <div className="p-4 text-sm text-slate-500">Nothing due this week.</div> : tasks.slice(0, 5).map(t => (
        <div key={t.id} className="flex items-center justify-between gap-2 px-4 py-2 border-t first:border-0 text-sm" data-testid={`dash-task-${t.id}`}>
          <span className="truncate">{t.title}</span><span className={`font-mono text-xs ${t.due < new Date().toISOString().slice(0, 10) ? "text-red-700" : "text-slate-500"}`}>{t.due}</span>
        </div>))}
    </CardContent></Card>
  );
}

const Alert = ({ label, value, href, tone, testId }) => (
  <a href={href} className={`flex items-center justify-between rounded-lg border px-4 py-2.5 text-sm ${tone}`} data-testid={testId}>
    <span>{label}</span><b className="font-mono">{value}</b>
  </a>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [glance, setGlance] = useState(null);
  const [pending, setPending] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const finance = ["Admin", "Accounts"].some(r => r === user?.role || (user?.roles || []).includes(r));
  const trustee = user?.role === "Trustee";

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => setStats({}));
    api.get("/approvals/pending").then(r => setPending(r.data.count ?? 0)).catch(() => setPending(0));
    if (!trustee) api.get("/mel/glance").then(r => setGlance(r.data)).catch(() => setGlance({ projects: [], tasks_week: [], open_advances: {} }));
    if (finance) {
      Promise.all([
        api.get("/v2/uc-reconciliation-warnings").then(r => r.data.warnings?.length || 0).catch(() => 0),
        api.get("/v2/payees/bank-missing").then(r => r.data.count).catch(() => 0),
        api.get("/v2/bank-requests").then(r => r.data.length).catch(() => 0),
        api.get("/advances/open-report").then(r => r.data.overdue_count).catch(() => 0),
      ]).then(([uc, bank, bankReq, overdue]) => setAlerts({ uc, bank, bankReq, overdue, verify: null }));
    }
  }, [user?.role]);

  const adv = glance?.open_advances;
  return (
    <div className="p-6 lg:p-8 space-y-5" data-testid="dashboard">
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-500 font-medium">Overview</div>
        <h1 className="font-display text-2xl sm:text-3xl font-extrabold mt-0.5">Welcome, {greetName(user?.name)}</h1>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat icon={Inbox} label="Pending on Me" value={pending} sub="items awaiting you" tone="blue" testId="stat-pending-on-me" href="/app/approvals" />
        <Stat icon={ListTodo} label="My Open Tasks" value={stats ? stats.pending_my_tasks ?? 0 : null} sub={stats ? `of ${stats.my_tasks ?? 0} total` : undefined} tone="amber" testId="stat-my-tasks" href="/app/tasks" />
        <Stat icon={HandCoins} label={adv?.scope === "all" ? "Open Advances" : "My Open Advances"} value={trustee ? "—" : adv ? `₹${(adv.amount || 0).toLocaleString("en-IN")}` : null}
          sub={adv ? `${adv.count} advance(s)` : undefined} tone="rose" testId="stat-open-advances" href={finance ? "/app/reports/open-advances" : "/app/advances"} />
        <Stat icon={Wallet} label="Budget Utilisation" value={stats ? `${stats.utilization_pct ?? 0}%` : null} sub="my projects" tone="emerald" testId="stat-utilization" href="/app/monitoring" />
      </div>
      {!trustee && <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2"><Glance rows={glance?.projects} /></div>
        <WeekTasks tasks={glance?.tasks_week} />
      </div>}
      {finance && <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="dash-alerts">
        {!alerts ? <><Skeleton className="h-10" /><Skeleton className="h-10" /><Skeleton className="h-10" /><Skeleton className="h-10" /></> : <>
          <Alert label="Missing bank details" value={alerts.bank} href="/app/masters?tab=bank" tone={alerts.bank ? "border-rose-300 bg-rose-50" : "border-slate-200"} testId="stat-bank-missing" />
          <Alert label="Bank details to verify" value={alerts.bankReq} href="/app/masters?tab=bankverify" tone={alerts.bankReq ? "border-amber-300 bg-amber-50" : "border-slate-200"} testId="stat-bank-pending" />
          <Alert label="Overdue settlements" value={alerts.overdue} href="/app/reports/open-advances" tone={alerts.overdue ? "border-rose-300 bg-rose-50" : "border-slate-200"} testId="dash-overdue-count" />
          <Alert label="UCs overdue" value={alerts.uc} href="/app/utilisation-certificate" tone={alerts.uc ? "border-amber-300 bg-amber-50" : "border-slate-200"} testId="uc-recon-warning" />
        </>}
      </div>}
      {trustee && <div className="text-sm text-slate-500 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Your approvals are in Pending on Me.</div>}
    </div>
  );
}
